import * as React from 'react'
import { addDays, format } from 'date-fns'
import { useNavigate, useParams } from 'react-router-dom'
import DailyNavigation from '../components/navigations/DailyNavigation'
import { useDate } from '@/modules/drivers/stores/dateStore'
import ActivityChartWrapper from '../components/charts/daily/ActivityChartWrapper'
import { ActivityInfo } from '@/app/models/activityContainerModel'
import useDriverActivityService from '@/modules/drivers/services/DriverActivityService'
import { useActivity } from '@/modules/drivers/stores/activityStore'

const Daily = ({
  showViolations,
  showTrips,
  showTachographActivities = true,
  showTachographEvents = true,
}: {
  showViolations?: boolean
  showTrips?: boolean
  showTachographActivities?: boolean
  showTachographEvents?: boolean
}) => {
  const {
    companyId,
    driverId,
    date: currentDate,
  } = useParams<{
    companyId: string
    driverId: string
    date: string
  }>()
  const navigate = useNavigate()
  const { setType, vehicleTachographMode, refreshTrigger } = useActivity()
  const { getDriverActivity } = useDriverActivityService()
  const { setSingle, formatApiDate } = useDate()

  const [data, setData] = React.useState<ActivityInfo | null>(null)
  const [rangeMode, setRangeMode] = React.useState(false)

  const [hourOffset, setHourOffset] = React.useState(0)

  const goToDate = (days: number) => {
    const [year, month, day] = currentDate.split('-').map(Number)
    const date = new Date(year, month - 1, day)

    if (isNaN(date.getTime())) {
      console.error('Invalid date:', currentDate)
      return
    }


    setHourOffset(0)
    const next = format(addDays(date, days), 'YYYY-MM-DD')
    navigate(
      `/et/company/${companyId}/driverModule/activities/driver/${driverId}/dailyActivity/${next}`,
    )
  }

  const goToHour = (hours: number) => {
    const next = hourOffset + hours
    if (next >= 24) {
      const [year, month, day] = currentDate.split('-').map(Number)
      const date = new Date(year, month - 1, day)
      if (isNaN(date.getTime())) return
      const nextDate = format(addDays(date, 1), 'YYYY-MM-DD')
      setHourOffset(next - 24)
      navigate(
        `/et/company/${companyId}/driverModule/activities/driver/${driverId}/dailyActivity/${nextDate}`,
      )
    } else if (next < 0) {
      const [year, month, day] = currentDate.split('-').map(Number)
      const date = new Date(year, month - 1, day)
      if (isNaN(date.getTime())) return
      const prevDate = format(addDays(date, -1), 'YYYY-MM-DD')
      setHourOffset(next + 24)
      navigate(
        `/et/company/${companyId}/driverModule/activities/driver/${driverId}/dailyActivity/${prevDate}`,
      )
    } else {
      setHourOffset(next)
    }
  }

  const getDailyActivity = async () => {
    try {
      const [year, month, day] = currentDate.split('-').map(Number)
      const dateObj = new Date(year, month - 1, day)
     
      const result = await getDriverActivity({
        companyId,
        driverId: Number(driverId),
        startTime: formatApiDate(dateObj),
        endTime: formatApiDate(addDays(dateObj, 2)),
        includeTrips: true,
        includeVehicleTachographData: false,
      })
      setData(result || null)
    } catch (error) {
      console.error('Daily: Error fetching daily activity:', error)
    }
  }

  React.useEffect(() => {
    setType('daily')
  }, [])

  React.useLayoutEffect(() => {
    getDailyActivity()
    setSingle(currentDate)
  }, [currentDate, driverId, vehicleTachographMode])

  React.useEffect(() => {
    if (refreshTrigger > 0) {
      getDailyActivity()
    }
  }, [refreshTrigger])

  return (
    <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
      <DailyNavigation
        currentDate={currentDate}
        goToDate={goToDate}
        onShiftHour={goToHour}
        rangeMode={rangeMode}
        onToggleRangeMode={() => setRangeMode((p) => !p)}
      />
      <ActivityChartWrapper
        showTrips={showTrips}
        rangeSelection={rangeMode}
        onRangeSelectionChange={setRangeMode}
        data={data}
        currentDate={currentDate}
        hourOffset={hourOffset}
        showViolations={showViolations}
        showTachographActivities={showTachographActivities}
        showTachographEvents={showTachographEvents}
        companyId={companyId}
        driverId={Number(driverId)}
        onRefresh={getDailyActivity}
      />
    </div>
  )
}

export default Daily
